'use client'

import { cn } from '@/lib/utils'
import { sanitizeForDisplay } from '@/lib/html-sanitizer'
import { trpc } from '@/lib/trpc'
import { ChevronLeft, ChevronRight, Quote, Star } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { useCallback, useEffect, useRef, useState } from 'react'

interface TestimonialsBlockContent {
  title?: string
  entryIds: string[]
  displayStyle?: 'cards' | 'carousel'
  showRatings?: boolean
}

interface TestimonialsBlockProps {
  content: TestimonialsBlockContent
  className?: string
}

export function TestimonialsBlock({ content, className }: TestimonialsBlockProps) {
  const { entryIds = [], displayStyle = 'cards', showRatings = true } = content
  const heading = content.title || 'What Fans Are Saying'

  const { data, isLoading } = trpc.guestBook.getTestimonialEntries.useQuery(
    { entryIds },
    { enabled: entryIds.length > 0 }
  )

  if (entryIds.length === 0) return null

  if (isLoading) {
    return (
      <div className={cn('space-y-4', className)}>
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Quote className="h-5 w-5 text-amber-400" />
          {heading}
        </h3>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: Math.min(3, entryIds.length) }).map((_, i) => (
            <Skeleton key={i} className="h-40 bg-zinc-800 rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  const entries = data?.entries || []
  if (entries.length === 0) return null

  if (displayStyle === 'carousel') {
    return (
      <div className={cn('space-y-4', className)}>
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Quote className="h-5 w-5 text-amber-400" />
          {heading}
        </h3>
        <TestimonialsCarousel entries={entries} showRatings={showRatings} />
      </div>
    )
  }

  return (
    <div className={cn('space-y-4', className)}>
      <h3 className="text-lg font-semibold text-white flex items-center gap-2">
        <Quote className="h-5 w-5 text-amber-400" />
        {heading}
      </h3>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {entries.map((entry) => (
          <TestimonialCard key={entry.id} entry={entry} showRatings={showRatings} />
        ))}
      </div>
    </div>
  )
}

// ============================================
// Carousel with navigation arrows and dots
// ============================================

function TestimonialsCarousel({ entries, showRatings }: {
  entries: TestimonialEntry[]
  showRatings: boolean
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 4)
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4)

    // Determine which card is most visible
    const cardWidth = 320 + 16 // w-80 + gap-4
    const index = Math.round(el.scrollLeft / cardWidth)
    setActiveIndex(Math.min(index, entries.length - 1))
  }, [entries.length])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    updateScrollState()
    el.addEventListener('scroll', updateScrollState, { passive: true })
    // Also update on resize since viewport width affects visibility
    window.addEventListener('resize', updateScrollState)
    return () => {
      el.removeEventListener('scroll', updateScrollState)
      window.removeEventListener('resize', updateScrollState)
    }
  }, [updateScrollState])

  const scrollTo = (direction: 'left' | 'right') => {
    const el = scrollRef.current
    if (!el) return
    const cardWidth = 320 + 16
    const offset = direction === 'left' ? -cardWidth : cardWidth
    el.scrollBy({ left: offset, behavior: 'smooth' })
  }

  const scrollToIndex = (index: number) => {
    const el = scrollRef.current
    if (!el) return
    const cardWidth = 320 + 16
    el.scrollTo({ left: index * cardWidth, behavior: 'smooth' })
  }

  return (
    <div className="relative group/carousel">
      {/* Scroll container */}
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3 scrollbar-none"
      >
        {entries.map((entry) => (
          <TestimonialCard
            key={entry.id}
            entry={entry}
            showRatings={showRatings}
            className="snap-start shrink-0 w-80"
          />
        ))}
      </div>

      {/* Left arrow */}
      {canScrollLeft && (
        <button
          onClick={() => scrollTo('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 w-8 h-8 rounded-full bg-zinc-800 border border-zinc-600 flex items-center justify-center text-zinc-300 hover:text-white hover:border-amber-500/50 hover:bg-zinc-700 transition-all opacity-0 group-hover/carousel:opacity-100 shadow-lg"
          aria-label="Previous testimonial"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      )}

      {/* Right arrow */}
      {canScrollRight && (
        <button
          onClick={() => scrollTo('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 w-8 h-8 rounded-full bg-zinc-800 border border-zinc-600 flex items-center justify-center text-zinc-300 hover:text-white hover:border-amber-500/50 hover:bg-zinc-700 transition-all opacity-0 group-hover/carousel:opacity-100 shadow-lg"
          aria-label="Next testimonial"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}

      {/* Dot indicators */}
      {entries.length > 1 && (
        <div className="flex items-center justify-center gap-1.5 pt-1">
          {entries.map((entry, i) => (
            <button
              key={entry.id}
              onClick={() => scrollToIndex(i)}
              className={cn(
                'rounded-full transition-all',
                i === activeIndex
                  ? 'w-5 h-1.5 bg-amber-400'
                  : 'w-1.5 h-1.5 bg-zinc-600 hover:bg-zinc-500',
              )}
              aria-label={`Go to testimonial ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface TestimonialEntry {
  id: string
  displayName: string
  message: string | null
  rating: number | null
  submittedAt: Date
  eventTitle: string
}

interface TestimonialCardProps {
  entry: TestimonialEntry
  showRatings: boolean
  className?: string
}

function TestimonialCard({ entry, showRatings, className }: TestimonialCardProps) {
  return (
    <div className={cn(
      'p-4 rounded-lg border border-zinc-700/50 bg-zinc-800/30',
      'hover:border-amber-500/30 transition-colors',
      className,
    )}>
      <Quote className="h-5 w-5 text-amber-400/50 mb-3" />

      {entry.message && (
        <div
          className="text-sm text-zinc-300 prose prose-sm prose-invert max-w-none mb-3 line-clamp-4"
          dangerouslySetInnerHTML={{ __html: sanitizeForDisplay(entry.message) }}
        />
      )}

      {showRatings && entry.rating && (
        <div className="flex items-center gap-0.5 mb-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star
              key={i}
              className={cn(
                'w-3.5 h-3.5',
                i < entry.rating!
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'text-zinc-600'
              )}
            />
          ))}
        </div>
      )}

      <div className="border-t border-zinc-700/50 pt-2 mt-auto">
        <p className="text-sm font-medium text-white">{entry.displayName}</p>
        <p className="text-xs text-zinc-500">{entry.eventTitle}</p>
      </div>
    </div>
  )
}
